import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { NotificationBanner } from 'src/components/widgets/notificationBanner';
import { T } from 'src/shared/helpers';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import DeleteIcon from 'src/shared/material_icons/action/delete.svg';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import EditIcon from 'src/shared/material_icons/editor/mode_edit.svg';
import RenameIcon from 'src/shared/material_icons/file/drive_file_rename_outline.svg';
import MoreVertIcon from 'src/shared/material_icons/navigation/more_vert.svg';
import styled, { useTheme } from 'styled-components';
import { BasicButton, GhostButton, PrimaryButton } from '../../../widgets/buttons';
import { Dialog, DialogWindow, QuestionDialog, useDialogManager } from '../../../widgets/dialog';
import { DropdownActionItem, DropdownActions } from '../../../widgets/dropdown';
import { EmptyList } from '../../../widgets/emptyList';
import { FormControl } from '../../../widgets/formControl';
import { Icon } from '../../../widgets/icon';
import { Input } from '../../../widgets/input';
import { showErrorNotification, showSuccessNotification } from '../../../widgets/notificationToaster';
import { PageHeader } from '../../../widgets/pageHeader';
import {
    ResourceCard,
    ResourceCardActions,
    ResourceCardFooter,
    ResourceCardsList,
    ResourceCardTitle,
    ResourceList,
    ResourceListEmptyResults
} from '../../../widgets/resourceList';
import { ZakekeLoading } from '../../../widgets/spinner';
import { ActionsCell, Row, Table } from '../../../widgets/table';
import {
    aiCustomAppsCreateMediaFactoryEditorEmbedMutation,
    aiCustomAppsCreateMediaFactoryEditorEmbedMutation$data
} from '../queries/__generated__/aiCustomAppsCreateMediaFactoryEditorEmbedMutation.graphql';
import { aiCustomAppsCreateMutation } from '../queries/__generated__/aiCustomAppsCreateMutation.graphql';
import { aiCustomAppsDeleteMutation } from '../queries/__generated__/aiCustomAppsDeleteMutation.graphql';
import { aiCustomAppsQuery as aiCustomAppsQueryType } from '../queries/__generated__/aiCustomAppsQuery.graphql';
import { aiCustomAppsRenameMutation } from '../queries/__generated__/aiCustomAppsRenameMutation.graphql';
import { aiCustomAppsUpdateMutation } from '../queries/__generated__/aiCustomAppsUpdateMutation.graphql';
import {
    aiCustomAppsQuery,
    createAICustomAppMutation,
    createMediaFactoryEditorEmbedMutation,
    deleteAICustomAppMutation,
    renameAICustomAppMutation,
    updateAICustomAppMutation
} from '../queries/aiCustomApps';
import type { MadooEditorBridge } from './customAppEmbedParams';
import {
    buildMadooBackofficeCss,
    createMadooEditorSaveRequestId,
    getMadooEmbedLocale,
    loadMadooEmbedSdk,
    parseMadooEditorSavedPayload,
    parseMadooEditorSaveFailedPayload
} from './customAppEmbedParams';
import { AI_STUDIO_CUSTOM_APP_SCOPE, AICustomApp, MadooEditorSavedPayload } from './customAppTypes';

const ManagerWindow = styled(DialogWindow)`
    width: calc(100vw - 24px);
    height: calc(100vh - 24px);
    max-width: none;
    max-height: none;
    flex-basis: auto;
    padding: 24px;
`;

const NameWindow = styled(DialogWindow)`
    width: min(420px, calc(100vw - 48px));
    flex-basis: auto;
    padding: 24px;
`;

const ManagerPageBody = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const AppName = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${(props) => props.theme.app.primary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const AppDescription = styled.div`
    margin-top: 4px;
    font-size: 12px;
    color: #57534e;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const NameDialogBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const TableWrapper = styled.div`
    overflow: auto;
    min-height: 0;
`;

const EditorShell = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const EditorMount = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    width: 100%;
    padding-bottom: 12px;

    iframe {
        flex: 1 1 0;
        min-height: 0;
        width: 100%;
    }
`;

const LoadingState = styled.div`
    flex: 1;
    min-height: 260px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const EmptyListWrapper = styled.div`
    flex: 1;
    min-height: 360px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

interface CustomAppsManagerDialogProps {
    scope?: string;
    onClose: () => void;
    onChanged: () => void;
    onDeleted?: (app: AICustomApp) => void;
    emptyStateSubtitle?: string;
}

interface CustomAppsManagerPageContentProps {
    scope?: string;
    pageTitle?: React.ReactNode;
    pageKey?: string;
    onChanged?: () => void;
    onDeleted?: (app: AICustomApp) => void;
    emptyStateSubtitle?: string;
}

interface CustomAppsManagerProps {
    scope?: string;
    pageTitle?: React.ReactNode;
    pageKey?: string;
    onClose?: () => void;
    onChanged?: () => void;
    onDeleted?: (app: AICustomApp) => void;
    emptyStateSubtitle?: string;
    pageMode?: boolean;
}

type CustomAppsManagerButton = {
    label: string;
    outline?: boolean;
    disabled?: boolean;
    onClick: () => void;
};

type EditorMode = { app?: AICustomApp; initialName?: string };

interface CustomAppNameDialogProps {
    title: string;
    initialName?: string;
    confirmLabel: string;
    onConfirm: (name: string) => void;
    onClose: () => void;
}

const CustomAppNameDialog: FC<CustomAppNameDialogProps> = ({ title, initialName = '', confirmLabel, onConfirm, onClose }) => {
    const validationContext = useValidationContext();
    const [name, setName] = useState(initialName);
    const trimmed = name.trim();

    const handleConfirm = () => {
        if (!validationContext.submit()) return;
        onConfirm(trimmed);
    };

    return (
        <Dialog
            windowDecorator={NameWindow}
            onClose={onClose}
            showCloseButton
            title={title}
            buttons={[
                {
                    label: T._('Cancel', 'Admin'),
                    outline: true,
                    onClick: onClose
                },
                {
                    label: confirmLabel,
                    onClick: handleConfirm
                }
            ]}
        >
            <NameDialogBody>
                <FormControl label={T._('Name', 'Admin')}>
                    <Input
                        fullWidth
                        value={name}
                        maxLength={200}
                        autoFocus
                        onChange={(event) => setName(event.currentTarget.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') handleConfirm();
                        }}
                        validationKey='customAppName'
                        validationLabel={T._('Name', 'Admin')}
                        validate={validateRequiredTrimmed}
                        validationMessage={T._('Name is required.', 'Admin')}
                    />
                </FormControl>
            </NameDialogBody>
        </Dialog>
    );
};

const CustomAppNameDialogWithValidation: FC<CustomAppNameDialogProps> = (props) => (
    <ValidationStateProvider>
        <CustomAppNameDialog {...props} />
    </ValidationStateProvider>
);

const CustomAppsManager: FC<CustomAppsManagerProps> = ({
    scope = AI_STUDIO_CUSTOM_APP_SCOPE,
    pageTitle = T._('Custom apps', 'Admin'),
    pageKey,
    onClose = () => undefined,
    onChanged = () => undefined,
    onDeleted,
    emptyStateSubtitle = T._('Create your first custom app to make it available in the AI Studio toolbar.', 'Admin'),
    pageMode = false
}) => {
    const theme = useTheme();
    const appsData = useLazyLoadQuery<aiCustomAppsQueryType>(aiCustomAppsQuery, { scope }, { fetchPolicy: 'store-and-network' });
    const apps = (appsData.aiCustomApps ?? []).filter((app): app is NonNullable<typeof app> => app !== null);
    const { showDialog, closeDialog } = useDialogManager();
    const [editorMode, setEditorMode] = useState<EditorMode | null>(null);
    const [editorEmbed, setEditorEmbed] = useState<aiCustomAppsCreateMediaFactoryEditorEmbedMutation$data['createMediaFactoryEditorEmbed'] | null>(null);
    const [editorError, setEditorError] = useState<string | null>(null);
    const [editorSaveRequestId, setEditorSaveRequestId] = useState<string | null>(null);
    const [isEditorBridgeReady, setEditorBridgeReady] = useState(false);
    const [search, setSearch] = useState('');
    const editorMountRef = useRef<HTMLDivElement | null>(null);
    const editorBridgeRef = useRef<MadooEditorBridge | null>(null);
    const pendingEditorSaveRequestIdRef = useRef<string | null>(null);

    const [commitEditorEmbed, isEditorEmbedLoading] = useMutation<aiCustomAppsCreateMediaFactoryEditorEmbedMutation>(createMediaFactoryEditorEmbedMutation);
    const [commitCreate, isCreating] = useMutation<aiCustomAppsCreateMutation>(createAICustomAppMutation);
    const [commitUpdate, isUpdating] = useMutation<aiCustomAppsUpdateMutation>(updateAICustomAppMutation);
    const [commitRename, isRenaming] = useMutation<aiCustomAppsRenameMutation>(renameAICustomAppMutation);
    const [commitDelete, isDeleting] = useMutation<aiCustomAppsDeleteMutation>(deleteAICustomAppMutation);

    const isSaving = isCreating || isUpdating || isRenaming;
    const isWaitingForEditorSave = editorSaveRequestId !== null;
    const normalizedSearch = search.trim().toLowerCase();
    const filteredApps = normalizedSearch
        ? apps.filter((app) => app.name.toLowerCase().includes(normalizedSearch) || (app.description ?? '').toLowerCase().includes(normalizedSearch))
        : apps;

    useEffect(() => {
        if (!editorMode) return;

        setEditorEmbed(null);
        setEditorError(null);
        setEditorSaveRequestId(null);
        setEditorBridgeReady(false);
        editorBridgeRef.current?.destroy();
        editorBridgeRef.current = null;
        pendingEditorSaveRequestIdRef.current = null;
        commitEditorEmbed({
            variables: {
                input: {
                    appId: editorMode.app?.mediaFactoryAppID
                }
            },
            onCompleted: (response) => {
                if (response.createMediaFactoryEditorEmbed) {
                    setEditorEmbed(response.createMediaFactoryEditorEmbed);
                } else {
                    setEditorError(T._('Unable to open editor.', 'Admin'));
                }
            },
            onError: () => setEditorError(T._('Unable to open editor.', 'Admin'))
        });
    }, [commitEditorEmbed, editorMode]);

    const handleSaved = useCallback(
        (payload: MadooEditorSavedPayload) => {
            const existingApp = editorMode?.app?.aiCustomAppID ? editorMode.app : undefined;
            const description = existingApp?.description ?? undefined;

            if (existingApp) {
                commitUpdate({
                    variables: {
                        input: {
                            aiCustomAppID: existingApp.aiCustomAppID,
                            scope,
                            name: payload.name,
                            description,
                            workflowGuid: payload.workflowGuid,
                            workflowInterfaceId: payload.workflowInterfaceId || '',
                            workflowExternalId: payload.workflowExternalId,
                            isActive: true
                        }
                    },
                    onCompleted: () => {
                        showSuccessNotification({ message: T._('Custom app saved.', 'Admin') });
                        onChanged();
                        setEditorMode(null);
                    },
                    updater: (store) => {
                        store.invalidateStore();
                    },
                    onError: () => {
                        const message = T._('Unable to save custom app.', 'Admin');
                        setEditorError(message);
                        showErrorNotification({ message });
                    }
                });
                return;
            }

            commitCreate({
                variables: {
                    input: {
                        scope,
                        name: payload.name,
                        description,
                        workflowGuid: payload.workflowGuid,
                        workflowInterfaceId: payload.workflowInterfaceId || '',
                        workflowExternalId: payload.workflowExternalId
                    }
                },
                onCompleted: () => {
                    showSuccessNotification({ message: T._('Custom app saved.', 'Admin') });
                    onChanged();
                    setEditorMode(null);
                },
                updater: (store) => {
                    store.invalidateStore();
                },
                onError: () => {
                    const message = T._('Unable to save custom app.', 'Admin');
                    setEditorError(message);
                    showErrorNotification({ message });
                }
            });
        },
        [commitCreate, commitUpdate, editorMode?.app, onChanged, scope]
    );

    const handleCreateNew = useCallback(() => {
        const dialogId = `create-ai-custom-app-name-${scope}`;
        showDialog(
            dialogId,
            <CustomAppNameDialogWithValidation
                title={T._('New custom app', 'Admin')}
                confirmLabel={T._('Create', 'Admin')}
                onClose={() => closeDialog(dialogId)}
                onConfirm={(name) => {
                    closeDialog(dialogId);
                    setEditorMode({ initialName: name });
                }}
            />
        );
    }, [closeDialog, scope, showDialog]);

    const requestEditorSave = useCallback(() => {
        if (!editorBridgeRef.current || isWaitingForEditorSave) return;

        const requestId = createMadooEditorSaveRequestId();
        pendingEditorSaveRequestIdRef.current = requestId;
        setEditorSaveRequestId(requestId);
        setEditorError(null);
        editorBridgeRef.current.save(requestId);
    }, [isWaitingForEditorSave]);

    useEffect(() => {
        const mount = editorMountRef.current;
        if (!editorMode || !editorEmbed?.editorUrl || !editorEmbed.sdkUrl || !mount) return;

        const editorUrl = editorEmbed.editorUrl;
        let cancelled = false;
        setEditorBridgeReady(false);
        editorBridgeRef.current?.destroy();
        editorBridgeRef.current = null;
        mount.replaceChildren();

        loadMadooEmbedSdk(editorEmbed.sdkUrl)
            .then((sdk) => {
                if (cancelled || !editorMountRef.current) return;

                editorBridgeRef.current = sdk.editor.create({
                    container: editorMountRef.current,
                    editorUrl,
                    token: editorEmbed.token,
                    workflowExternalId: editorEmbed.workflowExternalId ?? undefined,
                    name: editorMode.app?.name ?? editorMode.initialName,
                    locale: getMadooEmbedLocale(),
                    theme: {
                        primaryColor: theme.app.secondary
                    },
                    customCss: buildMadooBackofficeCss(theme),
                    width: '100%',
                    height: pageMode ? undefined : '100%',
                    allow: 'clipboard-read; clipboard-write',
                    showName: false,
                    showBranding: false,
                    onSaved: (payloadData) => {
                        const payload = parseMadooEditorSavedPayload(payloadData);
                        if (!payload) {
                            const message = T._('Unable to save custom app.', 'Admin');
                            setEditorError(message);
                            showErrorNotification({ message });
                            return;
                        }
                        if (!payload.requestId || payload.requestId !== pendingEditorSaveRequestIdRef.current) return;
                        pendingEditorSaveRequestIdRef.current = null;
                        setEditorSaveRequestId(null);
                        handleSaved(payload);
                    },
                    onSaveFailed: (payloadData) => {
                        const failedPayload = parseMadooEditorSaveFailedPayload(payloadData);
                        if (!failedPayload.requestId || failedPayload.requestId !== pendingEditorSaveRequestIdRef.current) return;

                        pendingEditorSaveRequestIdRef.current = null;
                        setEditorSaveRequestId(null);
                        const message = failedPayload.message ?? T._('Unable to save custom app.', 'Admin');
                        setEditorError(message);
                        showErrorNotification({ message });
                    },
                    onReady: () => setEditorBridgeReady(true)
                });
            })
            .catch(() => setEditorError(T._('Unable to open editor.', 'Admin')));

        return () => {
            cancelled = true;
            editorBridgeRef.current?.destroy();
            editorBridgeRef.current = null;
            setEditorBridgeReady(false);
            mount.replaceChildren();
        };
    }, [editorEmbed?.editorUrl, editorEmbed?.sdkUrl, editorEmbed?.token, editorMode, handleSaved, theme]);

    useEffect(() => {
        if (!editorBridgeRef.current) return;
        editorBridgeRef.current.setCustomCss(buildMadooBackofficeCss(theme));
    }, [theme]);

    const handleDelete = (app: AICustomApp) => {
        const dialogId = `delete-ai-custom-app-${app.aiCustomAppID}`;
        showDialog(
            dialogId,
            <QuestionDialog
                destructive
                message={T._('Delete this custom app?', 'Admin')}
                onYesClick={() => {
                    closeDialog(dialogId);
                    commitDelete({
                        variables: { aiCustomAppID: app.aiCustomAppID },
                        onCompleted: () => {
                            onDeleted?.(app);
                            onChanged();
                        },
                        updater: (store) => {
                            store.invalidateStore();
                        },
                        onError: () => setEditorError(T._('Unable to delete custom app.', 'Admin'))
                    });
                }}
            />
        );
    };

    const renderAppActions = (app: AICustomApp) => (
        <DropdownActions
            header={
                <GhostButton $type='icon' aria-label={T._('Actions', 'Admin')} disabled={isDeleting}>
                    <Icon>
                        <MoreVertIcon />
                    </Icon>
                </GhostButton>
            }
            items={[
                <DropdownActionItem
                    key='rename'
                    onClick={() => {
                        const dialogId = `rename-ai-custom-app-${app.aiCustomAppID}`;
                        showDialog(
                            dialogId,
                            <CustomAppNameDialogWithValidation
                                title={T._('Rename custom app', 'Admin')}
                                initialName={app.name}
                                confirmLabel={T._('Rename', 'Admin')}
                                onClose={() => closeDialog(dialogId)}
                                onConfirm={(name) => {
                                    closeDialog(dialogId);
                                    commitRename({
                                        variables: {
                                            input: {
                                                aiCustomAppID: app.aiCustomAppID,
                                                name
                                            }
                                        },
                                        onCompleted: () => onChanged(),
                                        updater: (store) => {
                                            store.invalidateStore();
                                        },
                                        onError: () => setEditorError(T._('Unable to rename custom app.', 'Admin'))
                                    });
                                }}
                            />
                        );
                    }}
                    disabled={isSaving || isDeleting}
                >
                    <Icon>
                        <RenameIcon />
                    </Icon>
                    {T._('Rename', 'Admin')}
                </DropdownActionItem>,
                <DropdownActionItem key='edit' onClick={() => setEditorMode({ app })} disabled={isDeleting}>
                    <Icon>
                        <EditIcon />
                    </Icon>
                    {T._('Edit app', 'Admin')}
                </DropdownActionItem>,
                <DropdownActionItem key='delete' danger onClick={() => handleDelete(app)} disabled={isDeleting}>
                    <Icon>
                        <DeleteIcon />
                    </Icon>
                    {T._('Delete', 'Admin')}
                </DropdownActionItem>
            ]}
            orientation='RIGHT'
            minWidth='180px'
        />
    );

    const buttons: CustomAppsManagerButton[] = editorMode
        ? [
              {
                  label: T._('Back', 'Admin'),
                  outline: true,
                  disabled: isSaving || isWaitingForEditorSave,
                  onClick: () => setEditorMode(null)
              },
              {
                  label: isSaving || isWaitingForEditorSave ? T._('Saving...', 'Admin') : T._('Save', 'Admin'),
                  disabled: isSaving || isWaitingForEditorSave || isEditorEmbedLoading || !editorEmbed?.editorUrl || !isEditorBridgeReady,
                  onClick: requestEditorSave
              }
          ]
        : [
              {
                  label: pageMode ? T._('Add', 'Admin') : T._('New', 'Admin'),
                  onClick: handleCreateNew
              }
          ];

    const content = (
        <>
            {editorMode ? (
                <EditorShell>
                    {editorError && <NotificationBanner type='ERROR' content={editorError} />}
                    {!editorError && (isEditorEmbedLoading || !editorEmbed?.editorUrl) && (
                        <LoadingState>
                            <ZakekeLoading />
                        </LoadingState>
                    )}
                    {editorEmbed?.editorUrl && <EditorMount ref={editorMountRef} aria-label={T._('Custom app editor', 'Admin')} />}
                </EditorShell>
            ) : apps.length === 0 ? (
                <EmptyListWrapper>
                    <EmptyList title={T._('No custom apps yet', 'Admin')} subTitle={emptyStateSubtitle} />
                </EmptyListWrapper>
            ) : pageMode ? (
                <ResourceList
                    searchValue={search}
                    searchPlaceholder={T._('Search custom apps...', 'Admin')}
                    viewModeLabel={T._('View mode', 'Admin')}
                    cardsViewLabel={T._('Show cards view', 'Admin')}
                    tableViewLabel={T._('Show table view', 'Admin')}
                    onSearchChange={setSearch}
                >
                    {(viewMode) =>
                        filteredApps.length === 0 ? (
                            <ResourceListEmptyResults>{T._('No custom apps found.', 'Admin')}</ResourceListEmptyResults>
                        ) : viewMode === 'cards' ? (
                            <ResourceCardsList>
                                {filteredApps.map((app) => (
                                    <ResourceCard
                                        key={app.aiCustomAppID}
                                        $clickable
                                        onClick={() => setEditorMode({ app })}
                                        role='button'
                                        tabIndex={0}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                setEditorMode({ app });
                                            }
                                        }}
                                    >
                                        <div>
                                            <ResourceCardTitle>{app.name}</ResourceCardTitle>
                                            <AppDescription>{app.description || T._('Custom app', 'Admin')}</AppDescription>
                                        </div>
                                        <ResourceCardFooter>
                                            <span />
                                            <ResourceCardActions onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                                                {renderAppActions(app)}
                                            </ResourceCardActions>
                                        </ResourceCardFooter>
                                    </ResourceCard>
                                ))}
                            </ResourceCardsList>
                        ) : (
                            <Table
                                headings={[
                                    [T._('Name', 'Admin'), '1fr'],
                                    [T._('Description', 'Admin'), '1fr'],
                                    ['', '50px']
                                ]}
                            >
                                {filteredApps.map((app) => (
                                    <Row
                                        key={app.aiCustomAppID}
                                        cells={[
                                            <AppName>{app.name}</AppName>,
                                            <AppDescription>{app.description || T._('Custom app', 'Admin')}</AppDescription>,
                                            <ActionsCell>{renderAppActions(app)}</ActionsCell>
                                        ]}
                                    />
                                ))}
                            </Table>
                        )
                    }
                </ResourceList>
            ) : (
                <TableWrapper>
                    <Table
                        headings={[
                            [T._('Name', 'Admin'), '1fr'],
                            [T._('Description', 'Admin'), '1fr'],
                            ['', '50px']
                        ]}
                    >
                        {apps.map((app) => (
                            <Row
                                key={app.aiCustomAppID}
                                cells={[
                                    <AppName>{app.name}</AppName>,
                                    <AppDescription>{app.description || T._('Custom app', 'Admin')}</AppDescription>,
                                    <ActionsCell>{renderAppActions(app)}</ActionsCell>
                                ]}
                            />
                        ))}
                    </Table>
                </TableWrapper>
            )}
        </>
    );

    if (pageMode) {
        return (
            <>
                <PageHeader
                    title={pageTitle}
                    pageKey={pageKey}
                    actions={
                        <>
                            {buttons.map((button, index) => {
                                const ButtonComponent = button.outline ? BasicButton : PrimaryButton;
                                return (
                                    <ButtonComponent
                                        key={`${index}-${button.label}`}
                                        type='button'
                                        $size='medium'
                                        disabled={button.disabled}
                                        onClick={button.onClick}
                                    >
                                        {!editorMode && !button.outline && (
                                            <Icon>
                                                <PlusIcon />
                                            </Icon>
                                        )}
                                        {button.label}
                                    </ButtonComponent>
                                );
                            })}
                        </>
                    }
                />
                <ManagerPageBody>{content}</ManagerPageBody>
            </>
        );
    }

    return (
        <Dialog windowDecorator={ManagerWindow} onClose={onClose} showCloseButton title={T._('Custom apps', 'Admin')} buttons={buttons}>
            {content}
        </Dialog>
    );
};

export const CustomAppsManagerDialog: FC<CustomAppsManagerDialogProps> = (props) => <CustomAppsManager {...props} />;

export const CustomAppsManagerPageContent: FC<CustomAppsManagerPageContentProps> = (props) => <CustomAppsManager {...props} pageMode />;
